import { Localized, useLocalization } from "@fluent/react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    IconButton,
    Snackbar,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
} from "@mui/material";
import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import { CancelButton } from "../../../app/components/CancelButton";
import { CloseButton } from "../../../app/components/CloseButton";
import { ReportBugButton } from "../../../app/components/ReportBugButton";
import { ScreenshotEditorDialog } from "../../../app/components/ScreenshotEditorDialog";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { blobToBase64, captureViewport, createGuid } from "../helpers/bugAttachmentHelpers";
import { addBug, selectSupportIntegration } from "../supportIntegrationSlice";
import { BugsList } from "./BugsList";

type Props = {
    open: boolean;
    onClose: () => void;
    initialScreenshot: Blob | null;
    initialCaptureError: boolean;
};

export const BugReportDialog = ({ open, onClose, initialScreenshot, initialCaptureError }: Props) => {
    const { l10n } = useLocalization();
    const dispatch = useAppDispatch();
    const { submitting, error: submitError } = useAppSelector(selectSupportIntegration);

    const [capturing, setCapturing] = useState(false);
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [screenLink, setScreenLink] = useState("");
    const [criticalFunctionalityUnavailable, setCriticalFunctionalityUnavailable] = useState(false);
    const [screenshot, setScreenshot] = useState<Blob | null>(null);
    const [includeScreenshot, setIncludeScreenshot] = useState(true);
    const [captureError, setCaptureError] = useState(false);
    const [editorOpen, setEditorOpen] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [activeTab, setActiveTab] = useState(0);

    useEffect(() => {
        if (!open) return;
        setActiveTab(0);
        setScreenLink(window.location.href);
        setScreenshot(initialScreenshot);
        setIncludeScreenshot(true);
        setCaptureError(initialCaptureError);
    }, [open, initialScreenshot, initialCaptureError]);

    const screenshotUrl = useMemo(
        () => screenshot ? URL.createObjectURL(screenshot) : null,
        [screenshot]
    );

    useEffect(() => () => {
        if (screenshotUrl) URL.revokeObjectURL(screenshotUrl);
    }, [screenshotUrl]);

    const takeScreenshot = useCallback(async () => {
        setCapturing(true);
        setCaptureError(false);

        try {
            setScreenshot(await captureViewport());
            setIncludeScreenshot(true);
        } catch {
            setScreenshot(null);
            setCaptureError(true);
        } finally {
            setCapturing(false);
        }
    }, []);

    const handleClose = useCallback(() => {
        if (!submitting) onClose();
    }, [onClose, submitting]);

    const handleSubmit = useCallback(async () => {
        const diagnosticData = [
            `Date: ${new Date().toISOString()}`,
            `Browser: ${navigator.userAgent}`,
        ].join("\n");

        const uploadedFiles = includeScreenshot && screenshot
            ? [{
                guid: createGuid(),
                ownerGuid: "00000000-0000-0000-0000-000000000000",
                fileName: `vchainer-bug-${new Date().toISOString().replace(/[:.]/g, "-")}.png`,
                contentType: screenshot.type || "image/png",
                fileData: await blobToBase64(screenshot),
            }]
            : undefined;

        const result = await dispatch(addBug({
            title: title.trim(),
            priority: criticalFunctionalityUnavailable ? "H" : "M",
            isBusinessCritical: criticalFunctionalityUnavailable,
            description: description.trim(),
            screenLink: screenLink.trim() || undefined,
            diagnosticData,
            uploadedFiles,
        }));

        if (addBug.fulfilled.match(result)) {
            onClose();
            setSubmitSuccess(true);
            setTitle("");
            setDescription("");
            setCriticalFunctionalityUnavailable(false);
            setScreenshot(null);
        }
    }, [criticalFunctionalityUnavailable, description, dispatch, includeScreenshot, onClose, screenLink, screenshot, title]);

    const canSubmit = title.trim().length > 0 && description.trim().length > 0 && !submitting;

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                fullWidth
                maxWidth="md"
                data-bug-report-exclude
            >
                <DialogTitle>
                    <Localized id="bug-report-title">
                        Report a bug
                    </Localized>
                </DialogTitle>


                <Tabs
                    value={activeTab}
                    onChange={(_, value: number) => setActiveTab(value)}
                    sx={{ px: 3, borderBottom: 1, borderColor: "divider" }}
                >
                    <Tab label={l10n.getString("bug-report-tab-new", null, "Report a bug")} />
                    <Tab label={l10n.getString("bug-report-tab-reported", null, "Reported bugs")} />
                </Tabs>


                <DialogContent>
                    {activeTab === 0
                        ? (
                            <Stack
                                spacing={3}
                                sx={{ mt: 2 }}
                            >
                        {/* REPORT DETAILS */}

                        <Stack spacing={2}>


                            <TextField
                                required
                                label={l10n.getString(
                                    "bug-report-subject",
                                    null,
                                    "Subject"
                                )}
                                value={title}
                                onChange={(event) =>
                                    setTitle(event.target.value)
                                }
                                inputProps={{
                                    maxLength: 160,
                                }}
                                fullWidth
                            />


                            <TextField
                                required
                                label={l10n.getString(
                                    "bug-report-description",
                                    null,
                                    "What happened?"
                                )}
                                value={description}
                                onChange={(event) =>
                                    setDescription(event.target.value)
                                }
                                minRows={4}
                                multiline
                                fullWidth
                            />


                            <TextField
                                label={l10n.getString(
                                    "bug-report-screen-link",
                                    null,
                                    "Screen link"
                                )}
                                value={screenLink}
                                onChange={(event) =>
                                    setScreenLink(event.target.value)
                                }
                                fullWidth
                            />


                            <Box
                                sx={{
                                    px: 2,
                                    py: 1.5,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 1,
                                }}
                            >
                                <FormControlLabel
                                    sx={{
                                        m: 0,
                                        alignItems: "flex-start",
                                    }}
                                    control={
                                        <Checkbox
                                            checked={
                                                criticalFunctionalityUnavailable
                                            }
                                            onChange={(event) =>
                                                setCriticalFunctionalityUnavailable(
                                                    event.target.checked
                                                )
                                            }
                                            sx={{
                                                mt: -0.5,
                                            }}
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography
                                                variant="body2"
                                                fontWeight={500}
                                            >
                                                <Localized id="bug-report-critical-functionality">
                                                    The problem blocks a critical business process
                                                </Localized>
                                            </Typography>

                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                component="div"
                                                sx={{ mt: 0.25 }}
                                            >
                                                <Localized id="bug-report-critical-functionality-description">
                                                    Select this option if the problem prevents you from continuing an important business process.
                                                </Localized>
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </Box>
                        </Stack>


                        <Divider />


                        {/* ATTACHMENTS */}

                        <Stack spacing={1.5}>


                            {captureError && (
                                <Alert severity="warning">
                                    <Localized id="bug-report-capture-error">
                                        The screenshot could not be created.
                                        You can still submit the report.
                                    </Localized>
                                </Alert>
                            )}


                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                spacing={1}
                            >
                                {screenshot
                                    ? (
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={
                                                        includeScreenshot
                                                    }
                                                    onChange={(event) =>
                                                        setIncludeScreenshot(
                                                            event.target.checked
                                                        )
                                                    }
                                                />
                                            }
                                            label={l10n.getString(
                                                "bug-report-include-screenshot",
                                                null,
                                                "Include screenshot"
                                            )}
                                        />
                                    )
                                    : (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            <Localized id="bug-report-no-screenshot">
                                                No screenshot attached
                                            </Localized>
                                        </Typography>
                                    )}


                                <Stack
                                    direction="row"
                                    spacing={1}
                                >
                                    {screenshot && (
                                        <Button
                                            size="small"
                                            startIcon={
                                                <EditOutlinedIcon />
                                            }
                                            onClick={() =>
                                                setEditorOpen(true)
                                            }
                                        >
                                            <Localized id="bug-report-edit-screenshot">
                                                Edit
                                            </Localized>
                                        </Button>
                                    )}


                                    <Button
                                        size="small"
                                        startIcon={
                                            <RefreshIcon />
                                        }
                                        onClick={takeScreenshot}
                                        disabled={capturing}
                                    >
                                        {capturing
                                            ? (
                                                <CircularProgress
                                                    size={16}
                                                />
                                            )
                                            : (
                                                <Localized id="bug-report-retake-screenshot">
                                                    Retake
                                                </Localized>
                                            )}
                                    </Button>


                                    {screenshot && (
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setScreenshot(null);
                                                setIncludeScreenshot(false);
                                            }}
                                            aria-label={l10n.getString(
                                                "delete",
                                                null,
                                                "Delete"
                                            )}
                                        >
                                            <DeleteOutlineIcon
                                                fontSize="small"
                                            />
                                        </IconButton>
                                    )}
                                </Stack>
                            </Stack>


                            {screenshot && screenshotUrl && (
                                <>
                                    <Box
                                        component="img"
                                        src={screenshotUrl}
                                        alt={l10n.getString(
                                            "bug-report-screenshot-preview",
                                            null,
                                            "Screenshot preview"
                                        )}
                                        sx={{
                                            display: "block",
                                            width: "100%",
                                            maxHeight: 360,
                                            objectFit: "contain",
                                            border: "1px solid",
                                            borderColor: "divider",
                                            borderRadius: 1,
                                            bgcolor: "grey.100",
                                        }}
                                    />


                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        <Localized id="bug-report-screenshot-privacy">
                                            Check the screenshot before submitting.
                                            Remove it if it contains sensitive data.
                                        </Localized>
                                    </Typography>
                                </>
                            )}
                        </Stack>


                        {submitError && (
                            <Alert severity="error">
                                <Localized id="bug-report-submit-error">
                                    The report could not be sent.
                                    Try again.
                                </Localized>
                            </Alert>
                        )}
                            </Stack>
                        )
                        : (
                            <Box sx={{ mt: 2 }}>
                                <BugsList mode="user" height="60vh" />
                            </Box>
                        )}
                </DialogContent>


                {activeTab === 0 && (
                    <DialogActions>
                        <CancelButton loading={false} onClick={handleClose} disabled={submitting} />
                        <ReportBugButton loading={submitting} onClick={handleSubmit}                         disabled={!canSubmit} />                    </DialogActions>
                )}
                {activeTab === 1 && (
                    <DialogActions>
                        <CloseButton loading={false} onClick={handleClose} />
                    </DialogActions>
                )}
            </Dialog>


            {screenshot && (
                <ScreenshotEditorDialog
                    open={editorOpen}
                    screenshot={screenshot}
                    onCancel={() =>
                        setEditorOpen(false)
                    }
                    onApply={(editedScreenshot) => {
                        setScreenshot(editedScreenshot);
                        setIncludeScreenshot(true);
                        setEditorOpen(false);
                    }}
                />
            )}


            <Snackbar
                open={submitSuccess}
                autoHideDuration={4000}
                onClose={() => setSubmitSuccess(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                data-bug-report-exclude
            >
                <Alert
                    severity="success"
                    variant="filled"
                    onClose={() => setSubmitSuccess(false)}
                >
                    <Localized id="bug-report-submit-success">
                        The report was sent successfully.
                    </Localized>
                </Alert>
            </Snackbar>
        </>
    );
};
